// V4-UX-SETUP-EXPRESS 2026-07-02 (Phase 1.1) — Onboarding « magique » : depuis
// l'URL du site de l'utilisateur, on enchaîne AUTOMATIQUEMENT les 3 premières
// étapes du setup (positionnement → centres d'intérêt → sources) en réutilisant
// les briques existantes :
//   - usePositioning().extractFromUrl  → POST /positioning/extract-from-url
//     (extrait le positionnement ET persiste landing_page_url + enqueue la charte
//      graphique côté backend — cf. extract.py:104-135)
//   - usePositioning().enrichPositioning → POST /positioning/enrich (LLM)
//   - usePositioning().updatePositioning → PUT /positioning/
//   - watchTopicsService.suggestTopics + create
//   - sourcesService.suggest + create
//
// La génération de posts n'est PAS déclenchée ici : le pipeline multi-agent exige
// un channel_id (GenerationRequest.channel_id: str, types.py:330). L'utilisateur
// connecte donc un réseau APRÈS ce setup express, puis génère via le flux normal.
// Le SetupWizard 4 étapes reste accessible en mode « manuel » (onSwitchToManual).
//
// Tailwind figé (V4-FRONT-TAILWIND-FROZEN-CSS) → styles inline pour tout le layout.
import { useState, useCallback, useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { usePositioning } from '../../../hooks/usePositioning';
import { watchTopicsService } from '../../../services/watchTopicsService';
import { sourcesService } from '../../../services/sourcesService';
import { toastService as toast } from '../../../services/toastService';
import { Logger } from '../../../utils/logger';
import { SIDEBAR_ITEMS } from '../../../utils/Constants';
import { mapSuggestedTypeToSourceType } from '../../../utils/sourceUtils';
import type { OnboardingData, CommunicationChannel } from '../../../types';
import {
  Sparkles, Globe, Loader2, CheckCircle2, Circle, AlertTriangle,
  Settings2, Target, Rss, ArrowRight, Wand2, Share2, FileText, MinusCircle,
} from 'lucide-react';

interface SetupExpressProps {
  spaceId: string;
  spaceName?: string;
  accountType?: string;
  steps: {
    hasPositioning: boolean;
    hasWatchTopic: boolean;
    hasSource: boolean;
    hasChannel: boolean;
  };
  /** Recharge les données du space (positionnement, topics, sources) côté parent.
   *  `silent` recharge en arrière-plan sans démonter l'arbre (évite la réapparition
   *  de la modale en fin de run — V4-UX-SETUP-EXPRESS-SILENT-REFRESH). */
  onRefresh: (opts?: { silent?: boolean }) => Promise<void> | void;
  /** Canaux de communication du space (pour détecter une connexion Zernio réelle). */
  communicationChannels?: CommunicationChannel[];
  /** Bascule vers le SetupWizard classique (mode manuel/avancé). */
  onSwitchToManual: () => void;
  /** CTA final quand un canal existe déjà (ouvre la génération). */
  onComplete: () => void;
  onOpenSidebarTab: (tabId: string) => void;
  isMobile?: boolean;
}

type StepId = 'positioning' | 'topics' | 'sources';
// 'notNeeded' : V4-ONBOARDING-PERSONAL-BRANDING — étape volontairement non exécutée
// (veille sectorielle désactivée pour ce profil), distincte de 'skipped' (déjà configuré).
type StepState = 'idle' | 'running' | 'done' | 'skipped' | 'failed' | 'notNeeded';
type Phase = 'intro' | 'running' | 'done' | 'error';
// V4-ONBOARDING-PERSONAL-BRANDING — source du positionnement : site web (chemin
// historique) ou personal branding (analyse des propres posts de l'utilisateur).
type SourceMode = 'website' | 'personal';

/** Découpe le textarea en posts individuels (séparateur = ligne vide ou `---`). */
function splitPosts(text: string): string[] {
  return text
    .split(/\n\s*\n|\n-{3,}\n/)
    .map(p => p.trim())
    .filter(p => p.length > 0);
}

/**
 * Vrai si une étape de contenu (thématiques / sources) a échoué pendant le run :
 * l'écran final ne doit alors PAS afficher « Votre espace est prêt ! » ✓.
 */
function hasFailedContentStep(stepStates: Record<StepId, StepState>): boolean {
  return stepStates.topics === 'failed' || stepStates.sources === 'failed';
}

const STEP_ICONS: Record<StepId, typeof Settings2> = {
  positioning: Settings2,
  topics: Target,
  sources: Rss,
};

export function SetupExpress({
  spaceId,
  spaceName,
  accountType,
  steps,
  onRefresh,
  onSwitchToManual,
  onComplete,
  onOpenSidebarTab,
  isMobile,
  communicationChannels = [],
}: SetupExpressProps) {
  const { t, language } = useLanguage();
  const { extractFromUrl, extractFromPosts, fetchConnectedPosts, enrichPositioning, updatePositioning } = usePositioning();

  const contentComplete = steps.hasPositioning && steps.hasWatchTopic && steps.hasSource;
  const needPositioning = !steps.hasPositioning;

  const [phase, setPhase] = useState<Phase>(contentComplete ? 'done' : 'intro');
  const [url, setUrl] = useState('');
  // V4-ONBOARDING-PERSONAL-BRANDING
  const [sourceMode, setSourceMode] = useState<SourceMode>('website');
  const [postsText, setPostsText] = useState('');
  // Veille sectorielle : ON par défaut pour le site web (le profil « entreprise »
  // s'appuie généralement sur l'actualité), laissé au choix en personal branding.
  const [useSectorWatch, setUseSectorWatch] = useState(true);
  // V4-ONBOARDING-PB-CONNECTED-IMPORT — état du bouton « importer mes posts ».
  const [importing, setImporting] = useState(false);
  const [stepStates, setStepStates] = useState<Record<StepId, StepState>>({
    positioning: 'idle',
    topics: 'idle',
    sources: 'idle',
  });
  const [counts, setCounts] = useState<{ topics: number; sources: number }>({ topics: 0, sources: 0 });

  const tx = t.setupExpress;
  const setStep = (id: StepId, s: StepState) => setStepStates(prev => ({ ...prev, [id]: s }));

  // V4-GEN-CHANNELLESS-DEMO (retiré) — un « canal réellement connecté » = ligne channel
  // AVEC compte Zernio connecté (même prédicat que PostCard.canDragPost / ZernioConnectButton
  // isConnected). `steps.hasChannel` ne suffit pas : une ligne channel peut exister sans
  // connexion effective → l'utilisateur ne pourrait pas publier.
  const hasConnectedChannel = communicationChannels.some(
    (c) => !!c.zernioAccountId && c.zernioStatus === 'connected',
  );

  // Toast d'avertissement une seule fois quand l'onboarding se termine (phase 'done')
  // alors qu'aucun canal n'est réellement connecté : sans réseau connecté, impossible
  // de publier. Remplace l'ancien mode démo.
  const warnedNoChannelRef = useRef(false);
  useEffect(() => {
    if (phase === 'done' && !hasConnectedChannel && !warnedNoChannelRef.current) {
      warnedNoChannelRef.current = true;
      toast.warning(
        tx?.noChannelWarning ??
          "Vous ne pourrez pas publier tant qu'un réseau n'est pas connecté à votre compte.",
      );
    }
  }, [phase, hasConnectedChannel, tx]);

  // V4-ONBOARDING-PB-CONNECTED-IMPORT — préremplit le textarea avec les posts
  // récents d'un compte social connecté (best-effort via Zernio).
  const importConnectedPosts = useCallback(async () => {
    setImporting(true);
    try {
      const { posts, source } = await fetchConnectedPosts(spaceId);
      if (posts.length === 0) {
        toast.info(tx?.importPostsEmpty ?? 'Aucun post à importer. Collez vos publications manuellement.');
        return;
      }
      const joined = posts.join('\n\n---\n\n');
      setPostsText(prev => (prev.trim() ? `${prev}\n\n---\n\n${joined}` : joined));
      const label = source?.username ? `@${source.username}` : (source?.network ?? '');
      toast.success(
        (tx?.importPostsDone ?? '{count} publications importées.').replace('{count}', String(posts.length))
        + (label ? ` (${label})` : ''),
      );
    } finally {
      setImporting(false);
    }
  }, [fetchConnectedPosts, spaceId, tx]);

  const runExpress = useCallback(async () => {
    // 1. Validation de la source du positionnement (site web OU posts perso).
    let normalizedUrl = url.trim();
    let personalPosts: string[] = [];
    if (needPositioning) {
      if (sourceMode === 'website') {
        if (normalizedUrl && !/^https?:\/\//i.test(normalizedUrl)) normalizedUrl = 'https://' + normalizedUrl;
        if (!/^https?:\/\/.+\..+/.test(normalizedUrl)) {
          toast.warning(tx?.urlInvalid ?? "L'adresse de votre site semble invalide.");
          return;
        }
      } else {
        personalPosts = splitPosts(postsText);
        if (personalPosts.length < 2) {
          toast.warning(tx?.postsInvalid ?? 'Collez au moins 2 publications, séparées par une ligne vide.');
          return;
        }
      }
    }

    // Veille sectorielle : imposée en mode site web, optionnelle en personal branding.
    const doVeille = sourceMode === 'website' || useSectorWatch;

    setPhase('running');
    setStepStates({ positioning: 'idle', topics: 'idle', sources: 'idle' });

    // 2. POSITIONNEMENT (extract → enrich → save). Bloquant : topics/sources en dépendent.
    if (needPositioning) {
      setStep('positioning', 'running');
      try {
        const res = sourceMode === 'website'
          ? await extractFromUrl(normalizedUrl, spaceId)
          : await extractFromPosts(personalPosts, spaceId, useSectorWatch);
        const extracted = (res as { extracted?: Record<string, unknown> } | undefined)?.extracted;
        if (!extracted) throw new Error('no_extract_data');
        const onboardingData: OnboardingData = {
          companyUrl: sourceMode === 'website' ? normalizedUrl : undefined,
          companyName: (extracted.company_name as string) || undefined,
          sector: (extracted.sector as string) || undefined,
          extractedData: extracted as unknown as OnboardingData['extractedData'],
        };
        const { positioning } = await enrichPositioning(onboardingData, language);
        await updatePositioning(positioning, 'onboarding_express', spaceId);
        setStep('positioning', 'done');
      } catch (e) {
        // 422 url_scraping_failed / posts_too_few / 504 timeout / matière insuffisante →
        // on ne peut pas construire le positionnement ⇒ écran d'erreur (réessayer /
        // configuration manuelle).
        Logger.warn('[SetupExpress] positioning extraction failed', e);
        setStep('positioning', 'failed');
        setPhase('error');
        return;
      }
    } else {
      setStep('positioning', 'skipped');
    }

    // Veille désactivée (personal branding sans actualité sectorielle) → on saute
    // proprement thématiques + sources (marquées « non nécessaires », pas « échouées »).
    if (!doVeille) {
      setStep('topics', 'notNeeded');
      setStep('sources', 'notNeeded');
      try {
        // Refresh silencieux : ne pas démonter cet arbre (sinon `phase` est perdu et
        // la modale réapparaît en `intro`) — V4-UX-SETUP-EXPRESS-SILENT-REFRESH.
        await onRefresh({ silent: true });
      } catch (e) {
        Logger.warn('[SetupExpress] onRefresh failed', e);
      }
      setPhase('done');
      return;
    }

    // 3. CENTRES D'INTÉRÊT (suggest → create). Nécessite le positionnement (OK ici).
    let topicsCreated = 0;
    if (!steps.hasWatchTopic) {
      setStep('topics', 'running');
      try {
        const { topics } = await watchTopicsService.suggestTopics(spaceId);
        for (const tp of topics ?? []) {
          try {
            await watchTopicsService.create(spaceId, {
              prompt: tp.name,
              keywords: tp.keywords ?? [],
              enabled: true,
            });
            topicsCreated++;
          } catch (err) {
            Logger.warn('[SetupExpress] topic create failed', err);
          }
        }
        setCounts(c => ({ ...c, topics: topicsCreated }));
        setStep('topics', topicsCreated > 0 ? 'done' : 'failed');
      } catch (e) {
        Logger.warn('[SetupExpress] suggestTopics failed', e);
        setStep('topics', 'failed');
      }
    } else {
      setStep('topics', 'skipped');
    }

    // 4. SOURCES (suggest → create). Nécessite positionnement + ≥1 topic.
    const canSources = steps.hasWatchTopic || topicsCreated > 0;
    if (steps.hasSource) {
      setStep('sources', 'skipped');
    } else if (canSources) {
      setStep('sources', 'running');
      try {
        const { sources } = await sourcesService.suggest(spaceId);
        let sourcesCreated = 0;
        for (const s of sources ?? []) {
          try {
            await sourcesService.create(spaceId, {
              title: s.name,
              url: s.url,
              // `newsletter` n'existe pas côté SourceType backend → WEBSITE.
              type: mapSuggestedTypeToSourceType(s.type),
              enabled: true,
            });
            sourcesCreated++;
          } catch (err) {
            Logger.warn('[SetupExpress] source create failed', err);
          }
        }
        setCounts(c => ({ ...c, sources: sourcesCreated }));
        setStep('sources', sourcesCreated > 0 ? 'done' : 'failed');
      } catch (e) {
        // 400 possible si l'ES n'a pas encore indexé le topic tout juste créé (lag NRT).
        Logger.warn('[SetupExpress] suggest/create sources failed', e);
        setStep('sources', 'failed');
      }
    } else {
      setStep('sources', 'failed');
    }

    // 5. Rafraîchit le parent pour refléter la nouvelle config (steps à jour).
    //    Silencieux : garde SetupExpress monté pour que `setPhase('done')` prenne
    //    effet (V4-UX-SETUP-EXPRESS-SILENT-REFRESH).
    try {
      await onRefresh({ silent: true });
    } catch (e) {
      Logger.warn('[SetupExpress] onRefresh failed', e);
    }
    setPhase('done');
  }, [url, sourceMode, postsText, useSectorWatch, needPositioning, spaceId, language,
      steps.hasWatchTopic, steps.hasSource,
      extractFromUrl, extractFromPosts, enrichPositioning, updatePositioning, onRefresh, tx]);

  // ---- Styles partagés (Tailwind figé → inline) ----
  const cardStyle: CSSProperties = {
    backgroundColor: 'var(--color-background)',
    borderRadius: '1rem',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    width: '100%',
    maxWidth: '640px',
    padding: isMobile ? '24px 16px' : '32px',
  };
  const primaryBtn: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    backgroundColor: 'var(--color-primary)', color: 'var(--color-on-primary, #fff)',
    borderRadius: '0.75rem', padding: '12px 20px', fontWeight: 600, border: 'none',
    cursor: 'pointer', width: '100%', fontSize: '15px',
  };
  const linkBtn: CSSProperties = {
    background: 'none', border: 'none', color: 'var(--color-text-secondary)',
    textDecoration: 'underline', cursor: 'pointer', fontSize: '14px', padding: '4px',
  };

  const renderIntro = () => {
    const startDisabled = needPositioning && (
      sourceMode === 'website' ? !url.trim() : splitPosts(postsText).length < 2
    );
    return (
    <div style={cardStyle} data-testid="setup-express">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '8px', marginBottom: '20px' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'var(--color-primary-light, rgba(85,0,255,0.1))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Sparkles size={26} style={{ color: 'var(--color-primary)' }} />
        </div>
        <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
          {tx?.title ?? 'Configurez Raiso en 2 minutes'}
        </h2>
        <p style={{ fontSize: '15px', color: 'var(--color-text-secondary)', margin: 0, maxWidth: 480 }}>
          {needPositioning
            ? (tx?.subtitle ?? "Donnez-nous l'adresse de votre site : on analyse votre activité, puis on vous propose automatiquement vos thématiques de veille et vos sources.")
            : (tx?.subtitlePositioningDone ?? 'Votre positionnement est prêt. On génère automatiquement vos thématiques de veille et vos sources.')}
        </p>
      </div>

      {accountType === 'AGENCY' && spaceName && (
        <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', textAlign: 'center', marginBottom: 16 }}>
          {(tx?.agencyContext ?? 'Configuration de l\'espace « {spaceName} ».').replace('{spaceName}', spaceName)}
        </p>
      )}

      {needPositioning && (
        <div style={{ marginBottom: 16 }}>
          {/* V4-ONBOARDING-PERSONAL-BRANDING — sélecteur de source du positionnement. */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {(['website', 'personal'] as SourceMode[]).map((mode) => {
              const active = sourceMode === mode;
              const Icon = mode === 'website' ? Globe : FileText;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setSourceMode(mode)}
                  data-testid={`setup-express-mode-${mode}`}
                  style={{
                    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '10px 8px', borderRadius: '0.75rem', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                    border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    backgroundColor: active ? 'var(--color-primary-light, rgba(85,0,255,0.08))' : 'transparent',
                    color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                  }}
                >
                  <Icon size={16} style={{ flexShrink: 0 }} />
                  {mode === 'website'
                    ? (tx?.sourceModeWebsite ?? "J'ai un site web")
                    : (tx?.sourceModePersonal ?? 'Personal branding')}
                </button>
              );
            })}
          </div>

          {sourceMode === 'website' ? (
            <>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 6 }}>
                {tx?.urlLabel ?? 'Adresse de votre site web'}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--color-border)', borderRadius: '0.75rem', padding: '10px 12px', backgroundColor: 'var(--color-surface, transparent)' }}>
                <Globe size={18} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
                <input
                  type="url"
                  inputMode="url"
                  autoFocus
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void runExpress(); }}
                  placeholder={tx?.urlPlaceholder ?? 'https://mon-entreprise.com'}
                  data-testid="setup-express-url"
                  style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: '15px', color: 'var(--color-text-primary)' }}
                />
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  {tx?.personalPostsLabel ?? 'Collez 3 à 5 de vos publications'}
                </label>
                {/* V4-ONBOARDING-PB-CONNECTED-IMPORT — import best-effort depuis un
                    compte social déjà connecté. Affiché seulement si un canal existe. */}
                {steps.hasChannel && (
                  <button
                    type="button"
                    onClick={() => void importConnectedPosts()}
                    disabled={importing}
                    data-testid="setup-express-import-posts"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
                      color: 'var(--color-primary)', cursor: importing ? 'default' : 'pointer',
                      fontSize: '12px', fontWeight: 600, padding: 0, opacity: importing ? 0.6 : 1,
                    }}
                  >
                    {importing
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Share2 size={14} />}
                    {tx?.importPostsCta ?? 'Importer mes derniers posts'}
                  </button>
                )}
              </div>
              <textarea
                value={postsText}
                onChange={(e) => setPostsText(e.target.value)}
                placeholder={tx?.personalPostsPlaceholder ?? 'Collez ici vos posts, séparés par une ligne vide entre chacun…'}
                data-testid="setup-express-posts"
                rows={7}
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'vertical',
                  border: '1px solid var(--color-border)', borderRadius: '0.75rem', padding: '10px 12px',
                  backgroundColor: 'var(--color-surface, transparent)', fontSize: '14px',
                  color: 'var(--color-text-primary)', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5,
                }}
              />
              <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', margin: '6px 0 0' }}>
                {tx?.personalPostsHint ?? 'On analyse votre style et vos thèmes pour construire votre positionnement.'}
              </p>

              {/* Veille sectorielle optionnelle en personal branding. */}
              <label
                data-testid="setup-express-sector-watch"
                style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={useSectorWatch}
                  onChange={(e) => setUseSectorWatch(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0, accentColor: 'var(--color-primary)' }}
                />
                <span>
                  <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                    {tx?.sectorWatchLabel ?? "Je m'appuie sur l'actualité de mon secteur"}
                  </span>
                  <span style={{ display: 'block', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                    {tx?.sectorWatchHint ?? 'Si activé, on crée des thématiques de veille et des sources. Sinon, on se base uniquement sur votre style.'}
                  </span>
                </span>
              </label>
            </>
          )}
        </div>
      )}

      <button
        style={{ ...primaryBtn, opacity: startDisabled ? 0.5 : 1 }}
        disabled={startDisabled}
        onClick={() => void runExpress()}
        data-testid="setup-express-start"
      >
        <Wand2 size={18} />
        {tx?.start ?? 'Configurer automatiquement'}
      </button>

      <div style={{ textAlign: 'center', marginTop: 14 }}>
        <button style={linkBtn} onClick={onSwitchToManual} data-testid="setup-express-manual">
          {tx?.manualLink ?? 'Je préfère configurer manuellement'}
        </button>
      </div>
    </div>
    );
  };

  const renderStepRow = (id: StepId) => {
    const state = stepStates[id];
    const StepIcon = STEP_ICONS[id];
    const label =
      id === 'positioning' ? (tx?.stepPositioning ?? 'Analyse de votre activité')
      : id === 'topics' ? (tx?.stepTopics ?? 'Thématiques de veille')
      : (tx?.stepSources ?? 'Sources de contenu');

    let sub = '';
    if (state === 'running') sub = tx?.stateRunning ?? 'En cours…';
    else if (state === 'done') {
      if (id === 'topics') sub = (tx?.topicsCreated ?? '{count} thématiques ajoutées').replace('{count}', String(counts.topics));
      else if (id === 'sources') sub = (tx?.sourcesCreated ?? '{count} sources ajoutées').replace('{count}', String(counts.sources));
      else sub = tx?.stateDone ?? 'Terminé';
    } else if (state === 'skipped') sub = tx?.stateSkipped ?? 'Déjà configuré';
    else if (state === 'notNeeded') sub = tx?.stateNotNeeded ?? 'Non nécessaire pour votre profil';
    else if (state === 'failed') sub = tx?.stateFailed ?? 'À compléter manuellement';

    return (
      <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ width: 22, height: 22, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {state === 'running' && <Loader2 size={20} className="animate-spin" style={{ color: 'var(--color-primary)' }} />}
          {(state === 'done' || state === 'skipped') && <CheckCircle2 size={20} style={{ color: 'var(--color-primary)' }} />}
          {state === 'notNeeded' && <MinusCircle size={18} style={{ color: 'var(--color-text-secondary)', opacity: 0.6 }} />}
          {state === 'failed' && <AlertTriangle size={18} style={{ color: 'var(--color-warning, #b45309)' }} />}
          {state === 'idle' && <Circle size={18} style={{ color: 'var(--color-text-secondary)', opacity: 0.4 }} />}
        </div>
        <StepIcon size={18} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}>{label}</div>
          {sub && <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{sub}</div>}
        </div>
      </div>
    );
  };

  const renderRunning = () => (
    <div style={cardStyle} data-testid="setup-express-running">
      <h2 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: 'var(--color-text-primary)', textAlign: 'center', marginTop: 0, marginBottom: 4 }}>
        {tx?.runningTitle ?? 'Préparation de votre espace…'}
      </h2>
      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', textAlign: 'center', marginBottom: 20 }}>
        {tx?.runningSubtitle ?? 'Cela prend moins d\'une minute.'}
      </p>
      <div>
        {(['positioning', 'topics', 'sources'] as StepId[]).map(renderStepRow)}
      </div>
    </div>
  );

  const renderError = () => (
    <div style={cardStyle} data-testid="setup-express-error">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8 }}>
        <AlertTriangle size={32} style={{ color: 'var(--color-warning, #b45309)' }} />
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
          {tx?.extractFailedTitle ?? "On n'a pas pu analyser votre site"}
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0, maxWidth: 460 }}>
          {tx?.extractFailedDesc ?? 'Le site est peut-être protégé ou trop lent. Configurons votre positionnement ensemble, étape par étape.'}
        </p>
      </div>
      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button style={primaryBtn} onClick={onSwitchToManual} data-testid="setup-express-error-manual">
          <Settings2 size={18} />
          {tx?.goManual ?? 'Configuration guidée'}
        </button>
        <button style={linkBtn} onClick={() => setPhase('intro')}>
          {tx?.retry ?? 'Réessayer avec une autre adresse'}
        </button>
      </div>
    </div>
  );

  // Run terminé mais thématiques et/ou sources en échec (ex. timeout de
  // /sources/suggest) : on montre l'état réel des étapes + relance / ajout manuel
  // au lieu d'un « prêt ! » trompeur.
  const renderIncomplete = () => {
    const topicsFailed = stepStates.topics === 'failed';
    return (
      <div style={cardStyle} data-testid="setup-express-incomplete">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8, marginBottom: 12 }}>
          <AlertTriangle size={32} style={{ color: 'var(--color-warning, #b45309)' }} />
          <h2 style={{ fontSize: isMobile ? '20px' : '22px', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
            {tx?.incompleteTitle ?? 'Configuration incomplète'}
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0, maxWidth: 480 }}>
            {tx?.incompleteDesc ?? "Certaines étapes n'ont pas abouti. Relancez la configuration automatique ou complétez-les manuellement."}
          </p>
        </div>
        <div>
          {(['positioning', 'topics', 'sources'] as StepId[]).map(renderStepRow)}
        </div>
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button style={primaryBtn} onClick={() => void runExpress()} data-testid="setup-express-retry">
            <Wand2 size={18} />
            {tx?.retryAuto ?? 'Relancer la configuration automatique'}
          </button>
          <button
            style={linkBtn}
            onClick={() => onOpenSidebarTab(topicsFailed ? SIDEBAR_ITEMS.WATCH : SIDEBAR_ITEMS.SOURCES)}
            data-testid="setup-express-complete-manually"
          >
            {topicsFailed
              ? (tx?.addTopicsManually ?? 'Ajouter mes thématiques manuellement')
              : (tx?.addSourcesManually ?? 'Ajouter mes sources manuellement')}
          </button>
        </div>
      </div>
    );
  };

  const renderDone = () => {
    if (hasFailedContentStep(stepStates)) return renderIncomplete();
    return (
      <div style={cardStyle} data-testid="setup-express-done">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'var(--color-primary-light, rgba(85,0,255,0.1))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle2 size={28} style={{ color: 'var(--color-primary)' }} />
          </div>
          <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
            {tx?.doneTitle ?? 'Votre espace est prêt !'}
          </h2>
          <p style={{ fontSize: '15px', color: 'var(--color-text-secondary)', margin: 0, maxWidth: 480 }}>
            {hasConnectedChannel
              ? (tx?.doneSubtitleChannel ?? 'Vous pouvez générer vos premiers posts dès maintenant.')
              : (tx?.doneSubtitleNoChannel ?? 'Connectez un réseau social pour générer puis publier vos posts.')}
          </p>
        </div>
        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {hasConnectedChannel ? (
            <button style={primaryBtn} onClick={onComplete} data-testid="setup-express-generate">
              <Wand2 size={18} />
              {tx?.ctaGenerate ?? 'Générer mes premiers posts'}
            </button>
          ) : (
            // Mode démo supprimé — sans canal réellement connecté, on redirige vers
            // l'onglet Channels au lieu de générer des posts d'exemple non publiables.
            <button style={primaryBtn} onClick={() => onOpenSidebarTab(SIDEBAR_ITEMS.CHANNELS)} data-testid="setup-express-connect">
              <Share2 size={18} />
              {tx?.ctaConnectChannel ?? 'Connecter un réseau social'}
              <ArrowRight size={16} />
            </button>
          )}
          <button style={linkBtn} onClick={onSwitchToManual}>
            {tx?.reviewSetup ?? 'Voir / ajuster ma configuration'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: isMobile ? '16px' : '24px' }}>
      {phase === 'intro' && renderIntro()}
      {phase === 'running' && renderRunning()}
      {phase === 'error' && renderError()}
      {phase === 'done' && renderDone()}
    </div>
  );
}
